/**
 * Fall painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const fallPaintingIdeasPack: ToilePack = {
    slug: 'fall-painting-ideas',
    category: 'craft',
    name: 'Fall painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'bridge', label: 'Covered bridge', subject: 'a canvas of a red covered bridge over a stream among blazing maples'},
        {key: 'patch', label: 'Pumpkin patch', subject: 'a canvas of a pumpkin patch at golden hour with a barn in the distance'},
        {key: 'birches', label: 'Birch grove', subject: 'a canvas of white birch trunks against orange and yellow autumn leaves'},
    ],
    styles: [
        {key: 'easy', label: 'Easy acrylic', style: 'painted in bold beginner acrylic with flat blocks of colour and sponge-dabbed foliage', palette: 'Saturated reds, oranges and golds against a clear sky.', framing: 'The canvas propped on a wooden table easel, shot straight on at eye level and centred.'},
        {key: 'knife', label: 'Palette knife', style: 'painted with a palette knife in thick impasto ridges, each leaf a single stroke of paint', palette: 'Rich rust, amber and crimson with cool blue-grey shadow notes.', framing: 'Hung above a stone mantel with a few small pumpkins below, shot at eye level.'},
        {key: 'folk', label: 'Folk art', style: 'painted in a naive folk-art style with simplified shapes, outlined details and tiny dotted leaves', palette: 'Warm cream ground with mustard, brick red and olive green.', framing: 'Leaning on a white shelf beside a candle and a jar of dried wheat, shot at eye level.'},
    ],
    directions: ['apple orchard', 'scarecrow', 'falling leaves'],
    directionHint: 'try: apple orchard',
    seo: {
        title: 'Fall Painting Ideas: Covered Bridge, Pumpkin Patch, Birches',
        metaDescription: 'Nine fall painting ideas in autumn color: a covered bridge among red maples, a pumpkin patch and a birch grove, in three styles.',
        h1: 'Fall painting ideas: covered bridge, pumpkin patch, birch grove',
        tagline: 'Nine finished fall canvases, three autumn scenes, each in beginner acrylic, palette knife or folk art.',
        intro: 'Fall is the season people most want to paint, for its color alone. These nine show three classic autumn scenes: a red covered bridge over a stream among blazing maples, a pumpkin patch at golden hour with a barn in the distance, and white birch trunks against orange and yellow leaves. Each comes as beginner acrylic, palette-knife impasto or folk art. Type another scene, such as an apple orchard or falling leaves, and the same season carries it.',
        useCases: [
            'an autumn wall for a hallway',
            'a mantel piece with dried wheat',
            'a gift for a fall birthday',
            'a classroom autumn project',
        ],
        faq: [
            {q: 'What is the folk-art style?', a: 'Simplified shapes, outlined details and tiny dotted leaves on a warm cream ground.'},
            {q: 'How is the palette-knife style different?', a: 'Each leaf is a single thick stroke of paint, so the foliage stands up off the canvas in ridges.'},
            {q: 'Can I change the scene?', a: 'Yes. Type another scene and the fall colors and the three styles carry it.'},
            {q: 'Where are the canvases shown?', a: 'Propped on a wooden table easel, hung above a stone mantel, or leaning on a white shelf beside a candle.'},
        ],
    },
}
